import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Center } from "@mantine/core";
import { toast } from "sonner";
import { usePortalEvent } from "@/lib/portal-socket";
import { callApiError, doorbellCallsApi } from "@/lib/api/doorbell-calls";
import {
  CALL_ENDED_EVENT,
  CALL_INCOMING_EVENT,
  activeIncoming,
  addIncoming,
  callIdFromPath,
  callIdOf,
  parseIncoming,
  removeIncoming,
  type IncomingCall,
} from "@/lib/doorbell-call/incoming";
import { getRingtone, hasInteracted } from "@/lib/doorbell-call/ringtone";
import { IncomingCallCard } from "./incoming-call-card";

interface ViewProps {
  call: IncomingCall;
  declining: boolean;
  onAnswer: (video: boolean) => void;
  onDecline: () => void;
}

/** The full-screen ringing banner — presentational. */
export function IncomingCallBannerView({ call, declining, onAnswer, onDecline }: ViewProps) {
  const titleId = `incoming-call-${call.callId}`;
  return (
    <Box
      pos="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="incoming-call"
      style={{ zIndex: 400, background: "var(--mantine-color-body)" }}
    >
      <Center h="100%" p="md">
        <IncomingCallCard
          askingFor={call.askingFor}
          location={call.location}
          isTest={call.isTest}
          onAnswer={onAnswer}
          decline={{ loading: declining, onDecline }}
          titleId={titleId}
        />
      </Center>
    </Box>
  );
}

/**
 * Mounted once in `AppShell`. Rings for every `call.incoming` on the portal
 * socket until the call is answered here, declined, `call.ended` arrives, or
 * its time is up. Hidden on the call page of the same call.
 */
export function IncomingCallBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [calls, setCalls] = useState<IncomingCall[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [declining, setDeclining] = useState<number | null>(null);

  usePortalEvent(CALL_INCOMING_EVENT, (e) => {
    const receivedAt = Date.now();
    const call = parseIncoming(e, receivedAt);
    if (!call) return;
    setNow(receivedAt);
    setCalls((list) => addIncoming(list, call));
  });
  usePortalEvent(CALL_ENDED_EVENT, (e) => {
    const callId = callIdOf(e);
    if (callId !== null) setCalls((list) => removeIncoming(list, callId));
  });

  // Close each call at its deadline.
  useEffect(() => {
    if (calls.length === 0) return;
    const next = Math.min(...calls.map((c) => c.deadline));
    const timer = setTimeout(() => {
      const t = Date.now();
      setNow(t);
      setCalls((list) => list.filter((c) => c.deadline > t));
    }, Math.max(0, next - Date.now()));
    return () => clearTimeout(timer);
  }, [calls]);

  const active = activeIncoming(calls, now, callIdFromPath(location.pathname));
  const activeId = active?.callId ?? null;

  // Ring while a call shows — once the member has touched the page (autoplay rules).
  useEffect(() => {
    if (activeId === null || !hasInteracted()) return;
    const ringtone = getRingtone();
    ringtone.start();
    return () => ringtone.stop();
  }, [activeId]);

  if (!active) return null;
  return (
    <IncomingCallBannerView
      call={active}
      declining={declining === active.callId}
      onAnswer={(video) => {
        setCalls((list) => removeIncoming(list, active.callId));
        navigate(`/doorbell/call/${active.callId}?answer=${video ? "video" : "voice"}`);
      }}
      onDecline={async () => {
        setDeclining(active.callId);
        try {
          await doorbellCallsApi.decline(active.callId);
        } catch (err) {
          toast.error(callApiError(err, "Couldn't decline the call."));
        } finally {
          setDeclining(null);
          setCalls((list) => removeIncoming(list, active.callId));
        }
      }}
    />
  );
}
